import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as d3 from 'd3';
import {
  X, FileCode2, Workflow, BookOpen, AlertCircle,
  ZoomIn, ZoomOut, Maximize2, Monitor, ShieldCheck, Server, Database, Cloud,
  Zap, Cog, Clock, Brain, CreditCard, TrendingUp, Mail, KeyRound, Waypoints,
} from 'lucide-react';

// Static system topology -- this is a hand-laid-out diagram of Stocker's real
// architecture, not an auto-generated graph. Positions are fixed (SVG
// coordinate space 0..1020 x 0..610) rather than force-directed, since a
// small, known set of real infrastructure nodes reads far more clearly laid
// out by hand than any generic layout algorithm would produce. Nodes are
// grouped into 5 zones so the diagram supports a "satellite" level of detail
// (zones only) as well as a zoomed-in level (individual systems) -- see the
// zoom/pan logic further down.
const NODES = [
  { id: 'frontend', label: 'Frontend', sub: 'React SPA', stat: '27 page dirs', x: 35, y: 245, w: 120, h: 110 },
  { id: 'nginx', label: 'nginx', sub: 'Reverse proxy · CSP · rate limits', stat: '1 config', x: 215, y: 245, w: 120, h: 110 },
  { id: 'backend', label: 'Backend API', sub: 'FastAPI', stat: '68 endpoint files', x: 405, y: 215, w: 170, h: 170 },

  { id: 'mysql', label: 'MySQL', sub: 'Primary DB', stat: '49 model files', x: 645, y: 35, w: 106, h: 115 },
  { id: 'redis', label: 'Redis', sub: 'Cache · sessions · CSRF · rate limits', stat: '3 logical DBs', x: 761, y: 35, w: 106, h: 115 },
  { id: 'celery_worker', label: 'Celery Worker', sub: 'Background jobs', stat: '17 task modules', x: 877, y: 35, w: 106, h: 115 },
  { id: 'celery_beat', label: 'Celery Beat', sub: 'Scheduler', stat: 'Cron-style beat', x: 703, y: 160, w: 106, h: 115 },
  { id: 'nlp', label: 'NLP / AI Enrichment', sub: 'OpenAI · stocker_nlp', stat: '2 clients', x: 819, y: 160, w: 106, h: 115 },

  { id: 'stripe', label: 'Stripe', sub: 'Payments', stat: 'Gated: MONETIZATION_LIVE=False', x: 645, y: 335, w: 106, h: 115 },
  { id: 'market_data', label: 'Market Data APIs', sub: 'Polygon · SEC/EDGAR', stat: 'Live provider', x: 761, y: 335, w: 106, h: 115 },
  { id: 'hcaptcha', label: 'hCaptcha', sub: 'Bot verification', stat: 'Registration gate', x: 877, y: 335, w: 106, h: 115 },
  { id: 'mailhog', label: 'Email Delivery', sub: 'SMTP', stat: 'MailHog (dev) / SES (prod)', x: 703, y: 460, w: 106, h: 115 },
  { id: 'oauth', label: 'OAuth Providers', sub: 'Google · GitHub', stat: 'Mounted, needs real keys', x: 819, y: 460, w: 106, h: 115 },
];

const EDGES = [
  ['frontend', 'nginx'],
  ['nginx', 'backend'],
  ['backend', 'mysql'],
  ['backend', 'redis'],
  ['backend', 'celery_worker'],
  ['backend', 'nlp'],
  ['celery_beat', 'celery_worker'],
  ['celery_worker', 'mysql'],
  ['backend', 'stripe'],
  ['backend', 'market_data'],
  ['backend', 'hcaptcha'],
  ['backend', 'mailhog'],
  ['backend', 'oauth'],
];

// The 5 "continents" of the satellite view. Bounding boxes are padded around
// their real children's positions above, so diving in from a zone never
// relocates anything -- the individual systems are exactly where the zone
// promised they'd be. Each zone carries a stable identity color (`accent`)
// that has nothing to do with health/severity -- it's "what this is", not
// "is it broken", so a zone with zero open issues still looks like a
// designed, alive part of the map instead of a dead gray box.
const ZONES = [
  { id: 'client', label: 'Client Layer', icon: Monitor, accent: '#6366f1', x: 20, y: 230, w: 150, h: 140, nodeIds: ['frontend'] },
  { id: 'edge', label: 'Edge / Proxy', icon: ShieldCheck, accent: '#0d9488', x: 200, y: 230, w: 150, h: 140, nodeIds: ['nginx'] },
  { id: 'app', label: 'Application Layer', icon: Server, accent: '#8b5cf6', x: 380, y: 190, w: 220, h: 220, nodeIds: ['backend'] },
  { id: 'data', label: 'Data & Background', icon: Database, accent: '#2563eb', x: 630, y: 20, w: 370, h: 270, nodeIds: ['mysql', 'redis', 'celery_worker', 'celery_beat', 'nlp'] },
  { id: 'external', label: 'External Services', icon: Cloud, accent: '#0891b2', x: 630, y: 320, w: 370, h: 270, nodeIds: ['stripe', 'market_data', 'hcaptcha', 'mailhog', 'oauth'] },
];

const ZONE_EDGES = [
  ['client', 'edge'],
  ['edge', 'app'],
  ['app', 'data'],
  ['app', 'external'],
];

// Per-system icon, purely identity -- same visual grammar as the zone tier
// so dropping from "zone" to "system" detail doesn't feel like a different
// diagram. Health/severity is communicated separately (border + left accent
// bar + badge), never by hiding or recoloring the icon itself.
const NODE_ICONS = {
  frontend: Monitor,
  nginx: Waypoints,
  backend: Server,
  mysql: Database,
  redis: Zap,
  celery_worker: Cog,
  celery_beat: Clock,
  nlp: Brain,
  stripe: CreditCard,
  market_data: TrendingUp,
  hcaptcha: ShieldCheck,
  mailhog: Mail,
  oauth: KeyRound,
};

// Which registry `component` values a given architecture node's health is
// judged by. Deliberately an approximation, not a 1:1 file-level trace --
// the expand panel below has the real per-node file references; this
// mapping only drives which nodes light up for a given open-issue category.
const COMPONENT_TO_NODES = {
  Security: ['backend', 'nginx'],
  '2FA / MFA': ['backend'],
  'Signup Integrity': ['backend', 'hcaptcha', 'mailhog'],
  Frontend: ['frontend'],
  Monetization: ['backend', 'stripe'],
  Auth: ['backend', 'oauth'],
  Gamification: ['backend', 'celery_worker'],
  Compliance: ['backend', 'mysql'],
  Credibility: ['backend', 'celery_worker'],
  Performance: ['backend', 'mysql', 'redis'],
  Mobile: ['frontend'],
  Infra: ['celery_worker', 'celery_beat', 'nlp'],
};

// Real, verified (not fabricated) per-node reference material -- every file
// path here was confirmed to exist on disk before being written in. This is
// what powers the "expand a node to see files/flow/docs" panel.
const NODE_DETAILS = {
  frontend: {
    description: 'React 18 SPA (Create React App + craco), served as static assets. Talks to the backend only via the REST API with cookie-based auth -- never directly to MySQL/Redis.',
    flow: [
      'Browser loads the SPA bundle',
      'React Router (routes.js / routesAdmin.js) matches a page',
      'Page component dispatches a Redux thunk',
      'Shared axios apiClient attaches CSRF token + cookies',
      'HTTP request leaves for nginx',
    ],
    files: [
      { path: 'frontend_stocker/src/routes.js', note: 'Top-level route table' },
      { path: 'frontend_stocker/src/pages/', note: '27 page directories -- Dashboard, Groups, Stocks, Settings, adminDashboard, ...' },
      { path: 'frontend_stocker/src/redux/', note: '30+ domain slices + thunks (Redux Toolkit)' },
      { path: 'frontend_stocker/src/redux/authentication/utils/core/axios.js', note: 'Shared API client + CSRF/refresh interceptors' },
      { path: 'frontend_stocker/src/PrivateRoutes.js', note: 'Auth route guard' },
    ],
    docs: ['Technical_Docs/11_FRONTEND_USER_FLOWS.md', 'Technical_Docs/12_REDUX_BACKEND_HOOKUP.md'],
  },
  nginx: {
    description: "Reverse proxy in front of both the frontend bundle and the backend API in production. Terminates TLS, sets security headers/CSP, and enforces per-route rate-limit zones.",
    flow: [
      'Request arrives, TLS terminated',
      'Security headers attached (CSP, HSTS, X-Frame-Options, ...)',
      'Per-location rate-limit zone checked (tight zones on /token, /users/register, /forgot-password, /reset-password/*)',
      'proxy_pass to the frontend static root or the backend API',
    ],
    files: [{ path: 'nginx/stocker.conf', note: 'The single real nginx config -- CSP, rate-limit zones, proxy_pass rules' }],
    docs: ['Technical_Docs/15_SECURITY_ASSESSMENT_2026.md'],
  },
  backend: {
    description: 'FastAPI (ASGI, served by Gunicorn + UvicornWorker). 68 endpoint files behind a layered middleware stack; admin-only routes gated by admin_required.',
    flow: [
      'ASGI request enters',
      'CORS -> CSRF (double-submit) -> global error handler -> rate limiting -> token blacklist check -> security headers -> SQL-injection guard',
      'Router dispatch to the matching endpoint file',
      "Endpoint's Depends() chain resolves (auth / db session / admin check)",
      'CRUD layer runs the actual DB work',
      'Response serialized and returned',
    ],
    files: [
      { path: 'backend_stocker/app/Main.py', note: 'App factory, middleware registration, all 68 router mounts' },
      { path: 'backend_stocker/app/api/v1/endpoints/', note: '68 endpoint files -- auth, posts, groups, admin, credibility, monetization, ...' },
      { path: 'backend_stocker/app/api/v1/dependencies/security/app_security.py', note: 'configure_app_security() -- the real middleware order' },
      { path: 'backend_stocker/app/crud/', note: 'DB access layer, one module per domain' },
      { path: 'backend_stocker/app/models/', note: '49 SQLAlchemy model files' },
    ],
    docs: ['Technical_Docs/02_CORE_CONFIG_SECURITY.md'],
  },
  mysql: {
    description: 'Primary relational store (InnoDB). Every table is versioned through Alembic with hand-written migrations, not blind autogenerate -- a past autogenerate silently missed columns.',
    flow: [
      'CRUD layer opens a SQLAlchemy Session via get_db()',
      'Query/mutate through the ORM',
      'Commit (or rollback on error)',
    ],
    files: [
      { path: 'backend_stocker/app/db/database.py', note: 'Engine + SessionLocal + get_db() dependency' },
      { path: 'backend_stocker/app/models/', note: '49 model files -- users, posts, groups, credibility, monetization, moderation, ...' },
      { path: 'backend_stocker/alembic/versions/', note: 'Full migration history' },
    ],
    docs: [],
  },
  redis: {
    description: '3 logical DBs on one Redis instance: auth (CSRF tokens, MFA session markers, token-blacklist fast path), rate-limit (per-user/IP counters), cache (@cacheable decorator).',
    flow: [
      'redis_client.py builds 3 separate clients from REDIS_AUTH_DB / REDIS_RATE_LIMIT_DB / REDIS_CACHE_DB',
      'Middleware/endpoint code imports whichever client matches its concern -- never one shared client',
    ],
    files: [
      { path: 'backend_stocker/app/api/v1/dependencies/redis_client.py', note: 'The 3 Redis clients' },
      { path: 'backend_stocker/app/api/v1/dependencies/security/middleware/rate_limiting.py', note: 'Rate-limit counters' },
      { path: 'backend_stocker/app/utils/redis_services/redis_decorator.py', note: '@cacheable decorator' },
    ],
    docs: [],
  },
  celery_worker: {
    description: '10 queues (celery, market_data, predictions, admin, news, maintenance, monitoring, credibility, context_radar, stock_updates), 17 task modules.',
    flow: [
      'A route handler or Celery Beat enqueues a task via .delay()/.apply_async()',
      'Task lands on Redis (the broker)',
      'A worker process bound to the matching queue picks it up',
      'Task module runs, writes results back to MySQL',
    ],
    files: [
      { path: 'backend_stocker/app/celery_app.py', note: 'Celery app + beat schedule' },
      { path: 'backend_stocker/app/utils/celery_tasks/', note: '17 task modules -- gamification, GDPR/export, monetization, market snapshots, ...' },
    ],
    docs: [],
  },
  celery_beat: {
    description: 'Cron-style scheduler that enqueues recurring Celery tasks -- nightly badge correlation, GDPR cleanup, monetization digests, market snapshots.',
    flow: [
      'celery_app.conf.beat_schedule fires on its configured cadence',
      'Enqueues the matching task onto its queue',
      'A Celery Worker process executes it',
    ],
    files: [{ path: 'backend_stocker/app/celery_app.py', note: 'beat_schedule dict -- every scheduled job and its cadence' }],
    docs: [],
  },
  nlp: {
    description: 'AI-enrichment pipeline: ticker/direction/target extraction and sentiment for group posts, plus a separate SEC filings NLP microservice.',
    flow: [
      'Group post created',
      'nlp_client.py calls the stocker_nlp microservice (OpenAI-backed), wrapped in a circuit breaker',
      'Entities/sentiment/predictions extracted, written to group_posts / predictions',
      'VADER sentiment (crud_vader_sentiment.py) runs independently as a non-AI fallback path',
    ],
    files: [
      { path: 'backend_stocker/app/services/nlp_client.py', note: 'Group-post AI enrichment client' },
      { path: 'backend_stocker/app/services/nlp_filings_client.py', note: 'SEC filings NLP client' },
      { path: 'backend_stocker/app/crud/crud_vader_sentiment.py', note: 'Independent VADER sentiment fallback' },
    ],
    docs: [],
  },
  stripe: {
    description: 'Creator payouts (Stripe Connect) + group subscriptions (Checkout). Hard-gated behind MONETIZATION_LIVE=False -- every path built and tested in Stripe test mode only.',
    flow: [
      'GroupPricing.jsx calls POST /groups/{id}/subscribe',
      'stripe_service.py builds a Checkout session',
      'Stripe redirects back, a webhook confirms',
      "platform_transactions row written at the creator's locked take rate",
    ],
    files: [
      { path: 'backend_stocker/app/utils/monetization/stripe_service.py', note: 'Stripe Checkout/Connect calls' },
      { path: 'backend_stocker/app/utils/monetization/take_rates.py', note: 'Tier-based take-rate table' },
      { path: 'backend_stocker/app/api/v1/endpoints/monetization_endpoint.py', note: 'Creator dashboard + webhooks' },
      { path: 'backend_stocker/app/api/v1/endpoints/group_monetization_endpoint.py', note: 'Pricing + subscribe' },
    ],
    docs: ['08_MASTER_TODO.md #0'],
  },
  market_data: {
    description: 'Real-time and historical market data. Polygon is the live provider today; SEC/EDGAR feeds filings data separately.',
    flow: [
      'MarketDataProvider interface abstracts the provider',
      'PolygonProvider is the only implementation wired up today',
      'stock_data / market_snapshots / indices tables kept warm by scheduled Celery tasks',
      "Dashboard's Market Overview widget reads them live",
    ],
    files: [
      { path: 'backend_stocker/app/utils/market_data_provider/base.py', note: 'Provider interface' },
      { path: 'backend_stocker/app/utils/market_data_provider/polygon_provider.py', note: 'The only implemented provider today' },
      { path: 'backend_stocker/app/crud/crud_sec.py', note: 'SEC/EDGAR filings' },
    ],
    docs: [],
  },
  hcaptcha: {
    description: "Bot-challenge on registration, added in the 2026-07-20 security-hardening pass. Defaults to hCaptcha's own published always-pass test keys so the flow is fully testable without a real account.",
    flow: [
      'RegisterPage.jsx renders the hCaptcha widget',
      'User solves it',
      'h_captcha_response submitted with the registration payload',
      "hcaptcha_verify.py POSTs to hCaptcha's real /siteverify endpoint before the account is created",
    ],
    files: [
      { path: 'backend_stocker/app/utils/hcaptcha_verify.py', note: 'Server-side verification' },
      { path: 'frontend_stocker/src/pages/Register/RegisterPage.jsx', note: 'Widget + honeypot field' },
    ],
    docs: ['13_FIX_HISTORY.md -- seventy-sixth pass, C.5'],
  },
  mailhog: {
    description: 'Transactional email -- verification links, password resets, notification digests. MailHog in dev (catches everything, nothing leaves the network); real SES in production.',
    flow: [
      'A route (e.g. /users/register, /forgot-password) calls send_email()',
      'FastMail (fastapi_mail) sends it',
      "settings.mail_config picks MailHog or SES by environment",
    ],
    files: [
      { path: 'backend_stocker/app/api/v1/endpoints/auth.py', note: 'send_email() + every auth-flow email' },
      { path: 'backend_stocker/app/core/config.py', note: 'mail_config -- MailHog vs SES switch' },
    ],
    docs: [],
  },
  oauth: {
    description: 'Google + GitHub social login. Fully built and mounted; blocked only on real client ID/secret (still placeholder strings) -- an account-ownership step, not a code gap.',
    flow: [
      'GET /api/v1/oauth/login/{provider}',
      'CSRF state stored in the session (SessionMiddleware)',
      'Redirect to the real provider',
      'Provider redirects back to /auth/{provider}',
      'handle_oauth_user() finds-or-creates the User, issues the same cookies as password login',
    ],
    files: [
      { path: 'backend_stocker/app/api/v1/dependencies/security/auth/oauth2.py', note: 'The full OAuth2 flow' },
      { path: 'frontend_stocker/src/pages/Login/OAuthCallbackPage.jsx', note: 'Frontend callback -- hydrates auth state from the cookies the backend already set' },
    ],
    docs: ['08_MASTER_TODO.md #1', '02_CORE_CONFIG_SECURITY.md §2.7'],
  },
};

const SEVERITY_RANK = { critical: 0, high: 1, medium: 2, low: 3 };
const SEVERITY_COLOR = {
  critical: { stroke: '#dc2626', fill: 'rgba(220,38,38,0.16)', dot: '#dc2626', label: 'Critical' },
  high: { stroke: '#ea580c', fill: 'rgba(234,88,12,0.16)', dot: '#ea580c', label: 'High' },
  medium: { stroke: '#d97706', fill: 'rgba(217,119,6,0.16)', dot: '#d97706', label: 'Medium' },
  low: { stroke: '#059669', fill: 'rgba(5,150,105,0.14)', dot: '#059669', label: 'Low' },
};
const HEALTHY_STROKE_DARK = '#475569';
const HEALTHY_STROKE_LIGHT = '#cbd5e1';

const VIEW_W = 1020;
const VIEW_H = 610;
const SCALE_EXTENT = [0.55, 2.75];
const NODES_VISIBLE_AT = 1.5; // k threshold: at/above this, individual systems render; below, zones do

function nodeCenter(node) {
  return { x: node.x + node.w / 2, y: node.y + node.h / 2 };
}

function zoneOf(nodeId) {
  return ZONES.find((z) => z.nodeIds.includes(nodeId));
}

// Smart connector: picks a horizontal or vertical dominant curve depending on
// where the two boxes actually sit relative to each other, so it still reads
// cleanly now that nodes are arranged in a grid/cluster rather than one row.
function edgePath(a, b) {
  const ac = nodeCenter(a);
  const bc = nodeCenter(b);
  const dx = bc.x - ac.x;
  const dy = bc.y - ac.y;
  if (Math.abs(dx) >= Math.abs(dy)) {
    const start = { x: dx >= 0 ? a.x + a.w : a.x, y: ac.y };
    const end = { x: dx >= 0 ? b.x : b.x + b.w, y: bc.y };
    const midX = (start.x + end.x) / 2;
    return `M ${start.x} ${start.y} C ${midX} ${start.y}, ${midX} ${end.y}, ${end.x} ${end.y}`;
  }
  const start = { x: ac.x, y: dy >= 0 ? a.y + a.h : a.y };
  const end = { x: bc.x, y: dy >= 0 ? b.y : b.y + b.h };
  const midY = (start.y + end.y) / 2;
  return `M ${start.x} ${start.y} C ${start.x} ${midY}, ${end.x} ${midY}, ${end.x} ${end.y}`;
}

function wrapLabel(label) {
  if (label.length <= 11) return [label];
  const mid = Math.floor(label.length / 2);
  let splitAt = label.lastIndexOf(' ', mid);
  if (splitAt === -1) splitAt = label.indexOf(' ', mid);
  if (splitAt === -1) return [label];
  return [label.slice(0, splitAt), label.slice(splitAt + 1)];
}

function truncate(text, maxChars) {
  if (!text || text.length <= maxChars) return text;
  return `${text.slice(0, Math.max(1, maxChars - 1))}…`;
}

// Generous, viewport-proportional padding (not a fixed pixel amount) so
// diving into a small single-node zone still leaves comfortable breathing
// room instead of blowing the lone card up edge-to-edge against its
// neighbor -- a real problem observed when this used a fixed 50px pad.
function computeZoneFitTransform(zone) {
  const zc = { x: zone.x + zone.w / 2, y: zone.y + zone.h / 2 };
  const padX = VIEW_W * 0.16;
  const padY = VIEW_H * 0.16;
  const scale = Math.min(SCALE_EXTENT[1], (VIEW_W - padX * 2) / zone.w, (VIEW_H - padY * 2) / zone.h);
  const tx = VIEW_W / 2 - scale * zc.x;
  const ty = VIEW_H / 2 - scale * zc.y;
  return d3.zoomIdentity.translate(tx, ty).scale(scale);
}

function zoneUnderViewportCenter(transform) {
  const cx = (VIEW_W / 2 - transform.x) / transform.k;
  const cy = (VIEW_H / 2 - transform.y) / transform.k;
  return ZONES.find((z) => cx >= z.x && cx <= z.x + z.w && cy >= z.y && cy <= z.y + z.h) || null;
}

const ArchitectureDiagram = ({ items, darkMode = true, onViewIssues }) => {
  const { t } = useTranslation();
  const [hoveredId, setHoveredId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [showNodes, setShowNodes] = useState(false);
  const [zoomPct, setZoomPct] = useState(100);
  const [focusZoneLabel, setFocusZoneLabel] = useState(null);

  const svgRef = useRef(null);
  const worldRef = useRef(null);
  const minimapViewportRef = useRef(null);
  const zoomBehaviorRef = useRef(null);
  const showNodesRef = useRef(false);

  // Wire up d3-zoom exactly once on mount -- transform is applied imperatively
  // to the inner <g ref={worldRef}> (and the minimap's viewport rect) so
  // React re-renders from item/theme changes never fight with, or reset,
  // the user's current pan/zoom position.
  useEffect(() => {
    if (!svgRef.current || !worldRef.current) return undefined;
    const svgSel = d3.select(svgRef.current);
    const worldEl = worldRef.current;

    const applyTransform = (transform) => {
      worldEl.setAttribute('transform', `translate(${transform.x},${transform.y}) scale(${transform.k})`);
      setZoomPct(Math.round(transform.k * 100));

      const shouldShowNodes = transform.k >= NODES_VISIBLE_AT;
      if (shouldShowNodes !== showNodesRef.current) {
        showNodesRef.current = shouldShowNodes;
        setShowNodes(shouldShowNodes);
      }

      const zone = zoneUnderViewportCenter(transform);
      setFocusZoneLabel(shouldShowNodes && zone ? zone.label : null);

      if (minimapViewportRef.current) {
        const vx = -transform.x / transform.k;
        const vy = -transform.y / transform.k;
        const vw = VIEW_W / transform.k;
        const vh = VIEW_H / transform.k;
        minimapViewportRef.current.setAttribute('x', Math.max(0, vx));
        minimapViewportRef.current.setAttribute('y', Math.max(0, vy));
        minimapViewportRef.current.setAttribute('width', Math.min(VIEW_W, vw));
        minimapViewportRef.current.setAttribute('height', Math.min(VIEW_H, vh));
      }
    };

    const zoomBehavior = d3.zoom()
      .scaleExtent(SCALE_EXTENT)
      .extent([[0, 0], [VIEW_W, VIEW_H]])
      .translateExtent([[-220, -220], [VIEW_W + 220, VIEW_H + 220]])
      .on('zoom', (event) => applyTransform(event.transform));

    zoomBehaviorRef.current = zoomBehavior;
    svgSel.call(zoomBehavior);
    svgSel.call(zoomBehavior.transform, d3.zoomIdentity);

    return () => {
      svgSel.on('.zoom', null);
    };
  }, []);

  const zoomBy = (factor) => {
    const svgSel = d3.select(svgRef.current);
    if (!zoomBehaviorRef.current) return;
    svgSel.transition().duration(280).call(zoomBehaviorRef.current.scaleBy, factor);
  };

  const resetView = () => {
    const svgSel = d3.select(svgRef.current);
    if (!zoomBehaviorRef.current) return;
    svgSel.transition().duration(450).call(zoomBehaviorRef.current.transform, d3.zoomIdentity);
  };

  const focusZone = (zone) => {
    const svgSel = d3.select(svgRef.current);
    if (!zoomBehaviorRef.current) return;
    svgSel.transition().duration(650).call(zoomBehaviorRef.current.transform, computeZoneFitTransform(zone));
  };

  const jumpTo = (vbX, vbY) => {
    const svgSel = d3.select(svgRef.current);
    if (!zoomBehaviorRef.current) return;
    const k = Math.max(SCALE_EXTENT[0], Math.min(SCALE_EXTENT[1], d3.zoomTransform(svgRef.current).k || 1));
    const target = d3.zoomIdentity.translate(VIEW_W / 2 - k * vbX, VIEW_H / 2 - k * vbY).scale(k);
    svgSel.transition().duration(400).call(zoomBehaviorRef.current.transform, target);
  };

  const { nodeHealth, zoneHealth } = useMemo(() => {
    const nHealth = {};
    NODES.forEach((n) => {
      nHealth[n.id] = { severity: null, openCount: 0, components: new Set() };
    });
    items.forEach((item) => {
      if (item.status === 'fixed') return; // only open/deferred items drive the coloring
      const nodeIds = COMPONENT_TO_NODES[item.component] || [];
      nodeIds.forEach((nid) => {
        const h = nHealth[nid];
        if (!h) return;
        h.openCount += 1;
        h.components.add(item.component);
        const rank = SEVERITY_RANK[item.severity] ?? 99;
        if (h.severity === null || rank < SEVERITY_RANK[h.severity]) h.severity = item.severity;
      });
    });
    const zHealth = {};
    ZONES.forEach((zone) => {
      let severity = null;
      let openCount = 0;
      let topItem = null;
      let topRank = 99;
      zone.nodeIds.forEach((nid) => {
        const h = nHealth[nid];
        openCount += h.openCount;
        if (h.severity && (severity === null || SEVERITY_RANK[h.severity] < SEVERITY_RANK[severity])) {
          severity = h.severity;
        }
      });
      items.forEach((item) => {
        if (item.status === 'fixed') return;
        const nodeIds = COMPONENT_TO_NODES[item.component] || [];
        if (!nodeIds.some((nid) => zone.nodeIds.includes(nid))) return;
        const rank = SEVERITY_RANK[item.severity] ?? 99;
        if (rank < topRank) {
          topRank = rank;
          topItem = item;
        }
      });
      zHealth[zone.id] = { severity, openCount, topItem };
    });
    return { nodeHealth: nHealth, zoneHealth: zHealth };
  }, [items]);

  const textColor = darkMode ? '#f1f5f9' : '#0f172a';
  const metaColor = darkMode ? '#94a3b8' : '#64748b';
  const edgeColor = darkMode ? '#334155' : '#cbd5e1';
  const zoneEdgeColor = darkMode ? '#3f4b5f' : '#c3cbd6';
  const canvasBg = darkMode ? '#0b1220' : '#f1f5f9';
  const dotColor = darkMode ? 'rgba(148,163,184,0.16)' : 'rgba(100,116,139,0.18)';
  const healthyStroke = darkMode ? HEALTHY_STROKE_DARK : HEALTHY_STROKE_LIGHT;
  const controlBg = darkMode ? 'bg-gray-800/90 hover:bg-gray-700 text-gray-200 border-gray-700' : 'bg-white/90 hover:bg-gray-50 text-gray-700 border-gray-200';

  const expandedNode = NODES.find((n) => n.id === expandedId) || null;

  return (
    <div>
      <div className="flex items-center gap-4 mb-3 flex-wrap">
        <span className={`text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('admin.architectureDiagram.legend')}</span>
        {Object.entries(SEVERITY_COLOR).map(([sev, c]) => (
          <span key={sev} className="flex items-center gap-1.5 text-xs" style={{ color: metaColor }}>
            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.dot }} />
            {t('admin.architectureDiagram.legendOpenIssue', { severity: c.label })}
          </span>
        ))}
        <span className="flex items-center gap-1.5 text-xs" style={{ color: metaColor }}>
          <span className="inline-block w-2.5 h-2.5 rounded-full border" style={{ borderColor: healthyStroke }} />
          {t('admin.architectureDiagram.noOpenIssues')}
        </span>
      </div>

      <style>{`
        @keyframes archGlowPulse { 0%, 100% { opacity: 0.18; } 50% { opacity: 0.55; } }
        .arch-glow { animation: archGlowPulse 2.4s ease-in-out infinite; }
        .arch-card { transform-box: fill-box; transform-origin: center; transition: transform 160ms ease; }
        .arch-card.hovered { transform: scale(1.028); }
        .arch-focusable:focus-visible { outline: 2px solid #3b82f6; outline-offset: 2px; border-radius: 8px; }
        @media (prefers-reduced-motion: reduce) {
          .arch-glow { animation: none; opacity: 0.32; }
          .arch-card { transition: none; }
        }
      `}</style>

      <div
        className="relative rounded-xl overflow-hidden border"
        style={{ backgroundColor: canvasBg, borderColor: darkMode ? '#1e293b' : '#e2e8f0', height: 'clamp(380px, 54vh, 580px)' }}
      >
        {/* Zoom controls */}
        <div className="absolute top-3 right-3 z-10 flex flex-col gap-1.5">
          <button type="button" onClick={() => zoomBy(1.45)} className={`p-2 rounded-lg border shadow-sm ${controlBg}`} aria-label={t('admin.architectureDiagram.zoomIn')} title={t('admin.architectureDiagram.zoomIn')}>
            <ZoomIn className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => zoomBy(1 / 1.45)} className={`p-2 rounded-lg border shadow-sm ${controlBg}`} aria-label={t('admin.architectureDiagram.zoomOut')} title={t('admin.architectureDiagram.zoomOut')}>
            <ZoomOut className="w-4 h-4" />
          </button>
          <button type="button" onClick={resetView} className={`p-2 rounded-lg border shadow-sm ${controlBg}`} aria-label={t('admin.architectureDiagram.fitWholeMap')} title={t('admin.architectureDiagram.fitWholeMap')}>
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>

        {/* Tier / breadcrumb indicator */}
        <div className={`absolute top-3 left-3 z-10 px-2.5 py-1.5 rounded-lg border text-[11px] font-semibold shadow-sm ${controlBg}`}>
          {focusZoneLabel ? t('admin.architectureDiagram.satelliteToZone', { zone: focusZoneLabel }) : t('admin.architectureDiagram.satelliteView')} · {zoomPct}%
        </div>

        <svg
          ref={svgRef}
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ width: '100%', height: '100%', display: 'block', cursor: 'grab', touchAction: 'none' }}
        >
          <defs>
            <pattern id="arch-dot-grid" width="26" height="26" patternUnits="userSpaceOnUse">
              <circle cx="1.4" cy="1.4" r="1.2" fill={dotColor} />
            </pattern>
            <radialGradient id="arch-vignette" cx="50%" cy="42%" r="75%">
              <stop offset="0%" stopColor={canvasBg} stopOpacity="0" />
              <stop offset="100%" stopColor={darkMode ? '#000000' : '#94a3b8'} stopOpacity={darkMode ? 0.35 : 0.12} />
            </radialGradient>
            <filter id="arch-node-shadow" x="-40%" y="-40%" width="180%" height="180%">
              <feDropShadow dx="0" dy="2" stdDeviation="4" floodOpacity={darkMode ? 0.45 : 0.18} />
            </filter>
            <filter id="arch-glow-blur" x="-60%" y="-60%" width="220%" height="220%">
              <feGaussianBlur stdDeviation="7" />
            </filter>
          </defs>

          <rect x="0" y="0" width={VIEW_W} height={VIEW_H} fill="url(#arch-dot-grid)" />
          <rect x="0" y="0" width={VIEW_W} height={VIEW_H} fill="url(#arch-vignette)" />

          <g ref={worldRef}>
            {/* ---------- Zone (satellite) tier ---------- */}
            <g style={{ opacity: showNodes ? 0 : 1, pointerEvents: showNodes ? 'none' : 'auto', transition: 'opacity 320ms ease' }}>
              <g fill="none" strokeWidth="2">
                {ZONE_EDGES.map(([fromId, toId]) => {
                  const from = ZONES.find((z) => z.id === fromId);
                  const to = ZONES.find((z) => z.id === toId);
                  if (!from || !to) return null;
                  return <path key={`${fromId}-${toId}`} d={edgePath(from, to)} stroke={zoneEdgeColor} strokeDasharray="1 7" strokeLinecap="round" opacity={0.9} />;
                })}
              </g>

              {ZONES.map((zone) => {
                const health = zoneHealth[zone.id];
                const severityColor = health.severity ? SEVERITY_COLOR[health.severity] : null;
                const isGlowing = health.severity === 'critical' || health.severity === 'high';
                const topIssueText = health.topItem ? `${SEVERITY_COLOR[health.topItem.severity]?.label || ''}: ${health.topItem.title}` : null;
                return (
                  <EntityCard
                    key={zone.id}
                    x={zone.x} y={zone.y} w={zone.w} h={zone.h} rx={28}
                    Icon={zone.icon}
                    iconBox={zone.id === 'app' ? 44 : 36}
                    iconSize={zone.id === 'app' ? 24 : 19}
                    accentColor={zone.accent}
                    titleLines={[zone.label]}
                    titleFontSize={zone.id === 'app' ? 16 : 14}
                    metaText={t('admin.architectureDiagram.systemsCount', { count: zone.nodeIds.length })}
                    extraText={topIssueText ? truncate(topIssueText, Math.floor(zone.w / 6)) : null}
                    severityColor={severityColor}
                    healthyStroke={healthyStroke}
                    openCount={health.openCount}
                    hovered={hoveredId === zone.id}
                    isGlowing={isGlowing}
                    darkMode={darkMode}
                    textColor={textColor}
                    metaColor={metaColor}
                    ariaLabel={t('admin.architectureDiagram.zoneAriaLabel', { zone: zone.label, count: health.openCount })}
                    onMouseEnter={() => setHoveredId(zone.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    onClick={() => focusZone(zone)}
                  />
                );
              })}
            </g>

            {/* ---------- Node (system detail) tier ---------- */}
            <g style={{ opacity: showNodes ? 1 : 0, pointerEvents: showNodes ? 'auto' : 'none', transition: 'opacity 320ms ease' }}>
              {/* Ghost zone outlines stay faintly visible for spatial context */}
              <g opacity={0.35}>
                {ZONES.map((zone) => (
                  <rect key={zone.id} x={zone.x} y={zone.y} width={zone.w} height={zone.h} rx={28} fill="none" stroke={zoneEdgeColor} strokeWidth={1.5} strokeDasharray="2 6" />
                ))}
              </g>

              <g fill="none" strokeWidth="1.75">
                {EDGES.map(([fromId, toId]) => {
                  const from = NODES.find((n) => n.id === fromId);
                  const to = NODES.find((n) => n.id === toId);
                  if (!from || !to) return null;
                  const isHighlighted = hoveredId === fromId || hoveredId === toId;
                  return (
                    <path
                      key={`${fromId}-${toId}`}
                      d={edgePath(from, to)}
                      stroke={isHighlighted ? '#3b82f6' : edgeColor}
                      strokeWidth={isHighlighted ? 2.75 : 1.75}
                      opacity={isHighlighted ? 1 : 0.8}
                    />
                  );
                })}
              </g>

              {NODES.map((node) => {
                const health = nodeHealth[node.id];
                const severityColor = health.severity ? SEVERITY_COLOR[health.severity] : null;
                const isGlowing = health.severity === 'critical' || health.severity === 'high';
                const isBackend = node.id === 'backend';
                return (
                  <EntityCard
                    key={node.id}
                    x={node.x} y={node.y} w={node.w} h={node.h} rx={16}
                    Icon={NODE_ICONS[node.id]}
                    iconBox={isBackend ? 46 : 32}
                    iconSize={isBackend ? 26 : 18}
                    accentColor={zoneOf(node.id)?.accent || '#6366f1'}
                    titleLines={wrapLabel(node.label)}
                    titleFontSize={isBackend ? 16 : 12.5}
                    metaText={truncate(node.sub, Math.max(10, Math.floor(node.w / 5.4)))}
                    extraText={null}
                    severityColor={severityColor}
                    healthyStroke={healthyStroke}
                    openCount={health.openCount}
                    hovered={hoveredId === node.id}
                    isGlowing={isGlowing}
                    darkMode={darkMode}
                    textColor={textColor}
                    metaColor={metaColor}
                    tooltip={t('admin.architectureDiagram.nodeTooltip', { label: node.label, sub: node.sub, stat: node.stat })}
                    ariaLabel={t('admin.architectureDiagram.nodeAriaLabel', { label: node.label, count: health.openCount })}
                    onMouseEnter={() => setHoveredId(node.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    onClick={() => showNodes && setExpandedId(node.id)}
                  />
                );
              })}
            </g>
          </g>

          {/* ---------- Minimap ---------- */}
          <g transform={`translate(${VIEW_W - 172}, ${VIEW_H - 112})`}>
            <rect x={0} y={0} width={160} height={100} rx={10} fill={darkMode ? 'rgba(15,23,42,0.85)' : 'rgba(255,255,255,0.9)'} stroke={darkMode ? '#334155' : '#cbd5e1'} strokeWidth={1.25} />
            <svg x={7} y={7} width={146} height={86} viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} style={{ overflow: 'visible', cursor: 'pointer' }}>
              {ZONES.map((zone) => (
                <rect key={zone.id} x={zone.x} y={zone.y} width={zone.w} height={zone.h} rx={16} fill={`${zone.accent}33`} stroke={zone.accent} strokeWidth={6} />
              ))}
              <rect
                ref={minimapViewportRef}
                x={0} y={0} width={VIEW_W} height={VIEW_H}
                fill="none" stroke={darkMode ? '#f8fafc' : '#1e293b'} strokeWidth={10}
              />
              <rect
                x={0} y={0} width={VIEW_W} height={VIEW_H} fill="transparent"
                onClick={(e) => {
                  const svgEl = e.currentTarget.ownerSVGElement;
                  const pt = svgEl.createSVGPoint();
                  pt.x = e.clientX;
                  pt.y = e.clientY;
                  const local = pt.matrixTransform(svgEl.getScreenCTM().inverse());
                  jumpTo(local.x, local.y);
                }}
              />
            </svg>
          </g>
        </svg>
      </div>
      <p className={`text-xs mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        {t('admin.architectureDiagram.caption')}
      </p>

      {expandedNode && (
        <NodeDetailPanel
          node={expandedNode}
          detail={NODE_DETAILS[expandedNode.id]}
          health={nodeHealth[expandedNode.id]}
          darkMode={darkMode}
          onClose={() => setExpandedId(null)}
          onViewIssues={() => {
            const components = Array.from(nodeHealth[expandedNode.id].components);
            setExpandedId(null);
            onViewIssues?.(components);
          }}
        />
      )}
    </div>
  );
};

// Shared render for both zone (satellite) and system (node) tiers, so the
// two levels of detail share one visual language: an accent-colored icon
// chip for "what this is" (stable, never affected by health), a left accent
// bar + border + badge for "is it healthy" (severity-driven), and a subtle
// hover lift so cards read as interactive before you ever click one.
function EntityCard({
  x, y, w, h, rx,
  Icon, iconBox, iconSize,
  accentColor,
  titleLines, titleFontSize,
  metaText, extraText,
  severityColor, healthyStroke,
  openCount, hovered, isGlowing,
  darkMode, textColor, metaColor,
  tooltip, ariaLabel,
  onMouseEnter, onMouseLeave, onClick,
}) {
  const stroke = severityColor ? severityColor.stroke : healthyStroke;
  const fill = severityColor ? severityColor.fill : `${accentColor}${darkMode ? '1c' : '12'}`;
  const barColor = severityColor ? severityColor.stroke : accentColor;
  // Every stacked flex child (icon chip, the title block, meta, extra) plus
  // the gaps between them -- undercounting this clips the icon chip inside
  // the foreignObject, since SVG clips foreignObject content to its box by
  // default. The title block is one flex child regardless of line count.
  const flexChildCount = 1 + 1 + (metaText ? 1 : 0) + (extraText ? 1 : 0);
  const titleBlockH = titleLines.length * (titleFontSize + 4);
  const contentH = iconBox + titleBlockH + (metaText ? 14 : 0) + (extraText ? 13 : 0) + (flexChildCount - 1) * 5 + 4;

  return (
    <g
      className="arch-focusable"
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      style={{ cursor: 'pointer' }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {tooltip && <title>{tooltip}</title>}
      {isGlowing && (
        <rect className="arch-glow" x={x - 7} y={y - 7} width={w + 14} height={h + 14} rx={rx + 6} fill={stroke} filter="url(#arch-glow-blur)" />
      )}
      <g className={`arch-card${hovered ? ' hovered' : ''}`}>
        <rect x={x} y={y} width={w} height={h} rx={rx} fill={fill} stroke={stroke} strokeWidth={hovered ? 2.5 : 1.75} filter="url(#arch-node-shadow)" />
        <rect x={x + 6} y={y + 10} width={4} height={h - 20} rx={2} fill={barColor} opacity={0.9} />

        <foreignObject x={x} y={y + h / 2 - contentH / 2} width={w} height={contentH}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 5 }}>
            <div
              style={{
                width: iconBox, height: iconBox, borderRadius: iconBox / 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: accentColor, flexShrink: 0,
                boxShadow: darkMode ? '0 1px 3px rgba(0,0,0,0.4)' : '0 1px 2px rgba(0,0,0,0.15)',
              }}
            >
              <Icon size={iconSize} color="#ffffff" strokeWidth={2.25} />
            </div>
            <div style={{ textAlign: 'center', lineHeight: 1.15 }}>
              {titleLines.map((line, i) => (
                <div key={i} style={{ color: textColor, fontWeight: 800, fontSize: titleFontSize }}>{line}</div>
              ))}
            </div>
            {metaText && <div style={{ color: metaColor, fontSize: Math.max(9, titleFontSize - 3), textAlign: 'center', padding: '0 8px' }}>{metaText}</div>}
            {extraText && (
              <div style={{ color: stroke, fontSize: 9.5, fontWeight: 600, textAlign: 'center', padding: '0 10px', maxWidth: '100%' }}>{extraText}</div>
            )}
          </div>
        </foreignObject>

        {openCount > 0 && (
          <g>
            <circle cx={x + w - (rx > 20 ? 16 : 12)} cy={y + (rx > 20 ? 16 : 12)} r={rx > 20 ? 12 : 9} fill={stroke} />
            <text x={x + w - (rx > 20 ? 16 : 12)} y={y + (rx > 20 ? 20.5 : 15.5)} textAnchor="middle" fontSize={rx > 20 ? 12 : 10} fontWeight="700" fill="#ffffff">
              {openCount}
            </text>
          </g>
        )}
      </g>
    </g>
  );
}

const NodeDetailPanel = ({ node, detail, health, darkMode, onClose, onViewIssues }) => {
  const { t } = useTranslation();
  if (!detail) return null;
  const cardBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const border = darkMode ? 'border-gray-700' : 'border-gray-200';
  const text = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-300' : 'text-gray-600';
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const chipBg = darkMode ? 'bg-gray-700/50' : 'bg-gray-100';
  const color = health.severity ? SEVERITY_COLOR[health.severity] : { dot: darkMode ? HEALTHY_STROKE_DARK : HEALTHY_STROKE_LIGHT };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
      onClick={onClose}
    >
      <div
        className={`w-full max-w-2xl rounded-xl border ${border} ${cardBg} shadow-2xl my-8`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex items-start justify-between p-5 border-b ${border}`}>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: color.dot }}
              />
              <h2 className={`text-xl font-extrabold ${text}`}>{node.label}</h2>
            </div>
            <p className={`text-xs ${textMuted}`}>{node.sub} · {node.stat}</p>
          </div>
          <button onClick={onClose} className={`p-1.5 rounded-md ${chipBg} ${textMuted} hover:${text}`}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
          <p className={`text-sm leading-relaxed ${textSecondary}`}>{detail.description}</p>

          <div>
            <h4 className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide ${textMuted} mb-2`}>
              <Workflow className="w-3.5 h-3.5" />
              {t('admin.architectureDiagram.detailPanel.requestFlow')}
            </h4>
            <ol className="space-y-1.5">
              {detail.flow.map((step, i) => (
                <li key={i} className={`text-sm flex gap-2 ${textSecondary}`}>
                  <span className={`flex-shrink-0 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center ${chipBg} ${textMuted}`}>{i + 1}</span>
                  <span className="pt-0.5">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <div>
            <h4 className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide ${textMuted} mb-2`}>
              <FileCode2 className="w-3.5 h-3.5" />
              {t('admin.architectureDiagram.detailPanel.realFiles')}
            </h4>
            <div className="space-y-1.5">
              {detail.files.map((f, i) => (
                <div key={i} className={`rounded-md px-3 py-2 ${chipBg}`}>
                  <div className={`text-xs font-mono ${text}`}>{f.path}</div>
                  <div className={`text-xs ${textMuted} mt-0.5`}>{f.note}</div>
                </div>
              ))}
            </div>
          </div>

          {detail.docs.length > 0 && (
            <div>
              <h4 className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide ${textMuted} mb-2`}>
                <BookOpen className="w-3.5 h-3.5" />
                {t('admin.architectureDiagram.detailPanel.relatedDocs')}
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {detail.docs.map((d, i) => (
                  <span key={i} className={`text-xs font-mono px-2 py-1 rounded ${chipBg} ${textSecondary}`}>{d}</span>
                ))}
              </div>
            </div>
          )}

          <div className={`flex items-center justify-between pt-4 border-t ${border}`}>
            <span className={`flex items-center gap-1.5 text-xs ${textMuted}`}>
              <AlertCircle className="w-3.5 h-3.5" />
              {t('admin.architectureDiagram.detailPanel.openIssuesTouch', { count: health.openCount })}
            </span>
            {health.openCount > 0 && (
              <button
                onClick={onViewIssues}
                className="px-3 py-1.5 rounded-md text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white"
              >
                {t('admin.architectureDiagram.detailPanel.viewRelatedIssues')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArchitectureDiagram;
